/** Query-key root for all transfer/ingest status caches (intake-job + source-import polling, my
 * submissions listing). Namespaces every uploads cache entry. */
export const UPLOADS_KEY = 'uploads' as const;

/**
 * Chunk size for the direct browser->GCS resumable PUT. GCS requires every non-final chunk to be a
 * multiple of 256 KiB; 32 MiB trades per-chunk progress granularity for fewer request round-trips.
 */
export const CHUNK_BYTES = 32 * 1024 * 1024;

/** Max concurrent direct uploads actively transferring bytes; the rest wait in the queue. */
export const MAX_CONCURRENT_UPLOADS = 8;

/** Maximum number of local files accepted from one picker action. The queue may contain these files
 * while the byte scheduler continues to enforce its smaller active-transfer concurrency. */
export const MAX_LOCAL_UPLOAD_FILES = 100;

/** The selected folder is depth zero. Files inside directories through depth six are eligible;
 * child directories encountered at depth six are not traversed. */
export const MAX_LOCAL_DIRECTORY_DEPTH = 6;

/** Maximum concurrent per-session reads while reconciling a rehydrated browser queue. */
export const UPLOAD_RECONCILE_CONCURRENCY = 4;

/** Automatic retries after a transient local-upload transport failure. The initial request is not
 * counted, so an operation can run at most six times before the row falls back to manual Retry. */
export const LOCAL_UPLOAD_MAX_AUTO_RETRIES = 5;

/** Nominal exponential retry schedule: 1s, 2s, 4s, 8s, 16s. Full jitter de-synchronizes clients. */
export const LOCAL_UPLOAD_RETRY_BASE_MS = 1_000;
export const LOCAL_UPLOAD_RETRY_MAX_MS = 30_000;

/** Maximum number of direct video uploads that may still be in progress at once. */
export const MAX_INCOMPLETE_UPLOADS = 20;

/** Maximum number of transfer rows rendered in either transfer-center surface. */
export const MAX_VISIBLE_TRANSFERS = 50;

/** Maximum cadence for publishing browser-local byte progress into React. Raw XHR/worker progress
 * remains in memory between ticks; durable queue state advances only after a committed chunk. */
export const UPLOAD_PROGRESS_PUBLISH_MS = 250;

/** Poll cadence (ms) for a non-terminal intake job. Stops once the job reaches a terminal state. */
export const INTAKE_POLL_MS = 2_000;

/** Source-import batch status cadence. Its first tick is immediate because the driver may reject a
 * remote source before any bytes move. */
export const SOURCE_IMPORT_STATUS_INITIAL_DELAY_MS = 0;
export const SOURCE_IMPORT_STATUS_POLL_MS = 10_000;

/** Local-upload lifecycle cadence. A delayed first tick and a different steady cadence keep the two
 * batch endpoints from firing in lockstep when a page restores both channels at once. */
export const LOCAL_UPLOAD_STATUS_INITIAL_DELAY_MS = 2_000;
export const LOCAL_UPLOAD_STATUS_POLL_MS = 12_000;

/** How long a submissions/status list stays fresh in the upload center. */
export const UPLOADS_STALE_TIME = 30_000;

/** Refresh cadence for the durable upload/import history shown on the `/transfers` page. */
export const UPLOADS_HISTORY_REFRESH_MS = 30_000;

/** A local upload whose backend/GCS committed offset does not advance across this window is treated
 * as interrupted. The session remains live so the original file can be reattached and resumed. */
export const LOCAL_UPLOAD_STALL_CHECK_MS = 10 * 60 * 1_000;

/** Retry cadence for an inconclusive GCS-offset probe after browser progress has already stalled. */
export const LOCAL_UPLOAD_STALL_PROBE_RETRY_MS = 30_000;

/** Avoid turning a large upload history into a burst of per-session `/upload-offset` requests. */
export const LOCAL_UPLOAD_OFFSET_PROBE_CONCURRENCY = 4;
